import { byPrefixAndName } from '@awesome.me/kit-f16c5d7bca/icons'
import { Box, Heading, Stack } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'

import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation } from 'react-router-dom-v7'
import heroBg from '../assets/images/backgrounds/hero-bg-icons.webp'
import MFLogo from '../components/logo'
import { useMFAdminAppInitializer } from '../contexts/MFAdminAppInitializer'

/**
 * The layout for the session-related routes.
 *
 * @param {Object} props
 * @param {boolean} props.isLoggedIn
 *
 * @returns {JSX.Element}
 */
const SessionsRoutes = () => {
  const {
    state: {
      layout: { skin }
    },
    actions: {
      layout: { SET_SKIN }
    }
  } = useMFAdminAppInitializer()

  const { pathname, search } = useLocation()

  const { auth, currentAdmin } = useSelector(state => ({
    auth: state.auth,
    currentAdmin: state.admins.current_admin
  }))

  const isLoggedIn = useMemo(
    () => !!(auth.isAuthenticated && currentAdmin?.id),
    [auth.isAuthenticated, currentAdmin?.id]
  )

  const sessionHeading = useMemo(() => {
    switch (pathname.split('/')[1]) {
      case 'login':
        if (search.includes('token_required=true')) return 'Two-Factor Authentication'
        return 'Sign In'
      case 'forgot_password':
        if (search.includes('reset_sent=true')) return 'Email Sent!'
        return 'Forgot Password'
      case 'reset_password':
        if (search.includes('reset=true')) return 'Password Updated!'
        return 'Create New Password'
      case 'auth0_callback':
        return 'Authenticating...'
      default:
        return ''
    }
  }, [pathname, search])

  const themeIcon = useMemo(() => (skin === 'light' ? 'moon' : 'sun'), [skin])
  const toggleTheme = useCallback(() => SET_SKIN(skin === 'light' ? 'dark' : 'light'), [SET_SKIN, skin])

  if (isLoggedIn) return <Navigate to="/dashboard" />

  return (
    <Box
      backgroundImage={`url(${heroBg})`}
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      display="flex"
      justifyContent="center"
      alignItems="center"
      h="100%"
      position="relative"
    >
      <Box
        position="absolute"
        top={4}
        right={4}
        w="fit-content"
        h="fit-content"
        color={{ base: 'neutral.900', md: 'whiteAlpha.800' }}
        cursor="pointer"
        transition="all 0.3s"
        _hover={{ color: { base: 'fg', md: 'white' } }}
        onClick={toggleTheme}
      >
        <FontAwesomeIcon size="lg" icon={byPrefixAndName['fad'][themeIcon]} />
      </Box>
      <Box
        p={{ base: 0, md: 2 }}
        h={{ base: '100%', md: 'auto' }}
        w={{ base: '100%', md: 'lg' }}
        maxH="100%"
        minH={{ base: '100%', md: 'auto' }}
        bg="whiteAlpha.400"
        borderRadius={{ base: 'none', md: 'xl' }}
        boxShadow={{ base: 'none', md: 'xl' }}
      >
        <Stack
          maxW={{ base: '100%', md: 'lg' }}
          h="100%"
          w="100%"
          py={{ base: '8', md: '8' }}
          px={{ base: '4', md: '10' }}
          justifyContent="center"
          alignItems="center"
          bg="surface"
          borderRadius={{ base: 'none', md: 'md' }}
        >
          <Stack spacing={8} maxW="lg" w="100%">
            <Stack as="header" spacing="6" align="center">
              <MFLogo size={280} />
              <Stack spacing="3" textAlign="center">
                <Heading as="h1" fontSize="3xl !important" fontWeight="light !important" mb="0" color="fg">
                  {sessionHeading}
                </Heading>
              </Stack>
            </Stack>
            <Outlet />
          </Stack>
        </Stack>
      </Box>
    </Box>
  )
}

SessionsRoutes.propTypes = {
  isLoggedIn: PropTypes.bool
}

export default SessionsRoutes
